import { getSiteLocation } from 'aos-config-loader'

import { Role } from './Role'

export const AosPermission = {
    AirportContactWrite: 'aos:airportcontact:write',
    AirportContactAdmin: 'aos:airportcontact:admin',
    DailyOpsRead: 'aos:dailyops:read',
    DailyOpsWrite: 'aos:dailyops:write',
    DailyOpsAdmin: 'aos:dailyops:admin',
    DiscountRead: 'aos:discount:read',
    DiscountWrite: 'aos:discount:write',
    DiscountAdmin: 'aos:discount:admin',
    EventRead: 'aos:event:read',
    EventWrite: 'aos:event:write',
    EventAdmin: 'aos:event:admin',
    ExternalAccess: 'aos:external:access',
    FirefighterRead: 'aos:firefighter:read',
    FirefighterWrite: 'aos:firefighter:write',
    FirefighterAdmin: 'aos:firefighter:admin',
    FlightAll: 'aos:flight:all',
    FlightDefault: 'aos:flight:default',
    FlightLimited: 'aos:flight:limited',
    FodRead: 'aos:fod:read',
    FodWrite: 'aos:fod:write',
    FodAdmin: 'aos:fod:admin',
    MessageRead: 'aos:message:read',
    MessageWrite: 'aos:message:write',
    MessageAdmin: 'aos:message:admin',
    ShiftNoteRead: 'aos:shiftnote:read',
    ShiftNoteWrite: 'aos:shiftnote:write',
    ShiftNoteAdmin: 'aos:shiftnote:admin',
    SystemAdmin: 'aos:system:admin',
    TaskRead: 'aos:task:read',
    TaskWrite: 'aos:task:write',
    TaskAdmin: 'aos:task:admin',
    UserRead: 'aos:user:read',
    UserWrite: 'aos:user:write',
    UserAdmin: 'aos:user:admin',
    WidgetConfigRead: 'aos:widgetconfig:read',
    WidgetConfigWrite: 'aos:widgetconfig:write',
    WidgetConfigAdmin: 'aos:widgetconfig:admin',
} as const

export type AosPermission = (typeof AosPermission)[keyof typeof AosPermission]

export interface DualReadGate {
    /** `aos:*` permissions that grant this capability. */
    permissions: readonly AosPermission[]
    /** Legacy roles that keep granting it while the tenant runs in dual-read mode. */
    legacyRoles: readonly string[]
    /** Surface had no gate in the legacy world — everyone passes while rbac is off. */
    legacyAllow?: boolean
}

/**
 * Tenant switch between the permission models, sourced from siteLocation
 * config (`aos-data-config.json`). `true` → gates pass ONLY via `aos:*`
 * permissions; `false`/absent → dual-read (permission OR legacy role, and
 * `legacyAllow` gates pass for everyone).
 *
 * Deliberately a lazy function: reading `getSiteLocation()` at module scope
 * would race `initConfigs()` and break per-file jest mocking.
 */
export const isRbacEnabled = (): boolean => getSiteLocation().rbacEnabled === true

/**
 * AOS-1269: tenant capability `moduleVisibility.global.fodListFollowsTaskAccess`.
 * Under rbac the FOD list follows the task gate (site roles / task admin, see
 * {@link canViewFodAt}) instead of fod-admin only. Finavia sets it; AOIMS
 * leaves it off and keeps the admin-only list (AOS-1082). Inert without
 * `rbacEnabled` — dual-read already folds FOD into tasks.
 */
export const fodListFollowsTaskAccess = (rbac: boolean = isRbacEnabled()): boolean =>
    rbac && getSiteLocation().moduleVisibility?.global?.fodListFollowsTaskAccess === true

/**
 * Single source of truth for the capability → (permission | legacy role |
 * legacy-ungated) mapping consumed by `CurrentUser`, `useGates`,
 * `useCanViewTasks` and `aos-mf`'s `useAuthInfo`. Three knobs per gate:
 * `permissions` (the rbac grant), `legacyRoles` (dual-read fallback) and
 * `legacyAllow` (surface had no gate before rbac). The tenant-level
 * `rbacEnabled` flag decides whether the legacy knobs are read at all —
 * flipping a tenant to rbac needs no edits here.
 */
export const DualReadGates = {
    eventAdmin: {
        permissions: [AosPermission.EventAdmin],
        legacyRoles: [Role.ApocUser, Role.EventAdmin, Role.AosAdmin],
    },
    userAdmin: {
        permissions: [AosPermission.UserAdmin],
        legacyRoles: [Role.ApocUser, Role.AosUserAdmin],
    },
    aosAdmin: {
        permissions: [AosPermission.SystemAdmin],
        legacyRoles: [Role.ApocUser, Role.AosAdmin],
    },
    /**
     * Task view/edit deliberately have NO gate here — a plain
     * `grantsGate(roles, …)` cannot express the backend task rule (site
     * roles + airport scope), and a permission-only `aos:task:read` gate is
     * exactly the AOS-1165 hole. Use {@link canViewTasksAt} /
     * {@link canEditTasksAt} — the single task gate.
     */
    taskAdmin: {
        permissions: [AosPermission.TaskAdmin],
        legacyRoles: [Role.TaskAdmin, Role.AosAdmin],
    },
    fodAdmin: {
        permissions: [AosPermission.FodAdmin],
        legacyRoles: [Role.FodAdmin],
    },
    discountEditor: {
        permissions: [AosPermission.DiscountWrite],
        legacyRoles: [Role.ApocUser, Role.AosAdmin, Role.CommercialBusinessUnit],
    },
    dashboardViewer: {
        permissions: [
            AosPermission.DailyOpsRead,
            AosPermission.DailyOpsWrite,
            AosPermission.DailyOpsAdmin,
        ],
        legacyRoles: [],
        legacyAllow: true,
    },
    eventViewer: {
        permissions: [AosPermission.EventRead, AosPermission.EventWrite, AosPermission.EventAdmin],
        legacyRoles: [],
        legacyAllow: true,
    },
    flightViewer: {
        permissions: [
            AosPermission.FlightAll,
            AosPermission.FlightDefault,
            AosPermission.FlightLimited,
        ],
        legacyRoles: [],
        legacyAllow: true,
    },
    /**
     * Contact list add/edit/delete — 1:1 with the backend
     * `@PreAuthorize(AIRPORTCONTACT_WRITE)` on both contact resources. The
     * list itself has NO gate (AOS-1276): the backend GET is open to every
     * authenticated principal, so the menu entry and route are ungated too.
     */
    contactEditor: {
        permissions: [AosPermission.AirportContactWrite, AosPermission.AirportContactAdmin],
        legacyRoles: [Role.ApocUser, Role.AosAdmin],
    },
    messagingEditor: {
        permissions: [
            AosPermission.MessageWrite,
            AosPermission.MessageAdmin,
            AosPermission.SystemAdmin,
        ],
        legacyRoles: [Role.ApocUser, Role.AosAdmin],
    },
    /**
     * FOD list by permission is admin-only: `aos:fod:read|write` is the
     * Finavia baseline (every authenticated user, AOS-1083 D1) and grants
     * reporting, never the list. Widening the list to task access goes
     * through {@link canViewFodAt} (tenant capability `fodListFollowsTaskAccess`),
     * not through this gate.
     */
    fodViewer: {
        permissions: [AosPermission.FodAdmin],
        legacyRoles: [],
    },
    fodEditor: {
        permissions: [AosPermission.FodWrite, AosPermission.FodAdmin],
        legacyRoles: [],
    },
} satisfies Record<string, DualReadGate>

/** Does `roles` (the raw claim) grant `gate` under the given permission model? */
export const grantsGate = (
    roles: readonly string[],
    gate: DualReadGate,
    rbac: boolean = isRbacEnabled(),
): boolean => {
    const viaPermission = gate.permissions.some(p => roles.includes(p))
    if (rbac) {
        return viaPermission
    }
    return (
        viaPermission || gate.legacyAllow === true || gate.legacyRoles.some(r => roles.includes(r))
    )
}

/**
 * Task-admin bypass, mirroring the backend `AosUserPrincipal.isTaskAdmin()`:
 * `TASK_ADMIN` / `AOS_ADMIN` / `aos:task:admin`. Unlike {@link grantsGate},
 * the legacy roles are honoured under BOTH permission models — the backend's
 * `PermissionChecker` never stops reading them.
 */
const isTaskAdminBypass = (roles: readonly string[]): boolean =>
    roles.includes(AosPermission.TaskAdmin) ||
    roles.includes(Role.TaskAdmin) ||
    roles.includes(Role.AosAdmin)

/**
 * AOS-1165 — exact frontend mirror of the backend task gate,
 * `PermissionChecker.hasPermission(siteLocation, principal, VIEW|EDIT, TASK)`:
 *
 *  1. task-admin bypass (`TASK_ADMIN` / `AOS_ADMIN` / `aos:task:admin`),
 *  2. legacy per-airport `${siteLocation}_VIEW|_EDIT` role — honoured under
 *     both permission models,
 *  3. only when the tenant enforces `aos:*` (mirrored here by `rbacEnabled`):
 *     `aos:task:read|write` AND the airport within the token's `groups`
 *     scope (`/airports/{IATA}`).
 *
 * Note `aos:task:read` alone is deliberately NOT enough: without airport
 * scope (or on a non-enforcing tenant) the backend rejects the request, so a
 * wider frontend gate would navigate users into a guaranteed 403 dead end.
 */
export const canViewTasksAt = (
    roles: readonly string[],
    airports: readonly string[],
    siteLocation: string,
    rbac: boolean = isRbacEnabled(),
): boolean =>
    isTaskAdminBypass(roles) ||
    roles.includes(`${siteLocation}_VIEW`) ||
    roles.includes(`${siteLocation}_EDIT`) ||
    (rbac &&
        (roles.includes(AosPermission.TaskRead) || roles.includes(AosPermission.TaskWrite)) &&
        airports.includes(siteLocation))

/** EDIT-level counterpart of {@link canViewTasksAt} (same backend gate, EDIT level). */
export const canEditTasksAt = (
    roles: readonly string[],
    airports: readonly string[],
    siteLocation: string,
    rbac: boolean = isRbacEnabled(),
): boolean =>
    isTaskAdminBypass(roles) ||
    roles.includes(`${siteLocation}_EDIT`) ||
    (rbac && roles.includes(AosPermission.TaskWrite) && airports.includes(siteLocation))

/**
 * AOS-1269 — the single FOD-list visibility rule, consumed by
 * `CurrentUser.canViewFod()` and `useCanViewFod`:
 *
 *  - `aos:fod:admin` always opens the list (AOIMS FOD_ADMIN),
 *  - dual-read: task visibility at the site ({@link canViewTasksAt}) — legacy
 *    FOD is folded into tasks,
 *  - rbac + `fodListFollowsTaskAccess` ({@link fodListFollowsTaskAccess},
 *    Finavia): task visibility at the site (`${siteLocation}_VIEW|_EDIT`, task
 *    admin, scoped `aos:task:read|write`),
 *  - rbac without the capability: admin-only (AOIMS pin, AOS-1082).
 *
 * `aos:fod:read|write` alone never opens the list: it is the Finavia baseline
 * every authenticated user carries for FOD reporting.
 */
export const canViewFodAt = (
    roles: readonly string[],
    airports: readonly string[],
    siteLocation: string,
    rbac: boolean = isRbacEnabled(),
): boolean =>
    grantsGate(roles, DualReadGates.fodViewer, rbac) ||
    ((!rbac || fodListFollowsTaskAccess(rbac)) &&
        canViewTasksAt(roles, airports, siteLocation, rbac))
